import { useMutation } from "@apollo/client";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import FlashContext from "components/FlashMessages/FlashContext";
import Button from "ds/components/Button";
import Tooltip from "ds/components/Tooltip";
import useTypedContext from "hooks/useTypedContext";

import { useRunShortcut } from "../../hooks/useRunShortcut";
import { RETRY_RUN } from "./gql";

type RetryRunButtonProps = {
  runId: string;
  stackId: string;
  runRetryBlocker: string | null;
  errorMessage?: string;
  isModuleRun?: boolean;
};

const RetryRunButton = ({
  runId,
  stackId,
  runRetryBlocker,
  errorMessage,
  isModuleRun,
}: RetryRunButtonProps) => {
  const { onError, reportSuccess } = useTypedContext(FlashContext);
  const navigate = useNavigate();

  const [retryRun, { loading, data }] = useMutation(RETRY_RUN, {
    variables: { stackId, runId },
    refetchQueries: ["GetRun"],
    awaitRefetchQueries: true,
  });

  const entityUrl = isModuleRun ? "module" : "stack";

  const onClick = useCallback(() => {
    retryRun()
      .then(({ data }) => {
        if (data?.runRetry?.id) {
          const runId = data.runRetry.id;
          reportSuccess({ message: `Retry run ${runId} successfully triggered` });
          navigate(`/${entityUrl}/${stackId}/run/${runId}`);
        }
      })
      .catch(onError);
  }, [retryRun, onError, reportSuccess, navigate, stackId, entityUrl]);

  const isLoading = loading || !!data?.runRetry?.id;
  const isDisabled = !!runRetryBlocker || !!errorMessage;
  const disabledMessage = errorMessage || runRetryBlocker;
  const isTooltipActive = !!disabledMessage || isModuleRun;
  const tooltipMessage =
    disabledMessage || (isModuleRun ? "Initial config will be used for retrying the run." : "");

  useRunShortcut({
    label: "Retry",
    code: "mod+alt+r",
    callback: onClick,
    disabled: isDisabled,
    loading: isLoading,
  });

  return (
    <Tooltip
      on={(props) => (
        <Button
          {...props}
          disabled={isDisabled}
          loading={isLoading}
          onPress={onClick}
          variant="secondary"
          size="small"
        >
          Retry
        </Button>
      )}
      active={isTooltipActive}
    >
      {tooltipMessage}
    </Tooltip>
  );
};

export default RetryRunButton;
